import React from 'react';
import { truncateDescription } from '../utils/truncateDescription';
import { LiveIndicator } from './LiveIndicator';

interface DemoCardProps {
  demo: {
    id: string;
    title: string;
    description: string;
    projectName: string;
    category: string;
    createdAt: string;
    createdBy: string;
    hasVideo: boolean;
    videoFileName?: string | null;
    artifacts?: Array<{ name: string; mimeType: string; size: number }>;
    hasLiveDemo: boolean;
    liveDemoUrl: string | null;
    webUrl: string;
  };
}

/**
 * Renders an individual demo entry card.
 */
export const DemoCard: React.FC<DemoCardProps> = ({ demo }) => {
  const formattedDate = new Date(demo.createdAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const artifactCount = demo.artifacts?.length || 0;

  return (
    <article className="demo-card" role="listitem">
      <div className="demo-card__header">
        <h3 className="demo-card__title">{demo.title}</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {demo.hasVideo && (
            <span className="demo-card__video-badge" title="Has demo video">🎬</span>
          )}
          {demo.hasLiveDemo && demo.liveDemoUrl && (
            <LiveIndicator liveDemoUrl={demo.liveDemoUrl} />
          )}
        </div>
      </div>

      <p className="demo-card__description">
        {truncateDescription(demo.description)}
      </p>

      {/* Meta info */}
      <div className="demo-card__meta">
        {demo.hasVideo && demo.videoFileName && (
          <span className="demo-card__tag demo-card__tag--video">
            📹 {demo.videoFileName}
          </span>
        )}
        {artifactCount > 0 && (
          <span className="demo-card__tag">
            📎 {artifactCount} artifact{artifactCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="demo-card__footer">
        <span className="demo-card__project">
          📁 {demo.category}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="demo-card__author">{demo.createdBy}</span>
          <time className="demo-card__date" dateTime={demo.createdAt}>
            {formattedDate}
          </time>
        </div>
      </div>
    </article>
  );
};
